import { isEmpty, isNil } from 'lodash';
import { EditingLock } from 'map-3d/shared/map-3d-tools/drawing-tools';
import { WorkspaceTools } from 'map-3d/shared/map-3d-tools/enums';
import { type GeometryType } from 'shared/cesium';
import {
  DESELECT_FEATURE_INSTRUCTION,
  DRAW_COMPLETION_INSTRUCTION_TOOLS,
  DRAWING_INSTRUCTION,
  EDITING_HANDLES_HIDDEN_INSTRUCTION,
  REMOVE_VERTEX_INSTRUCTION,
} from './constants';
import {
  type ToolboxShortcutDefinition,
  type WorkspaceInstructionState,
} from './types';

/**
 * Banner instruction for the current selection: how to deselect, plus how to
 * remove a vertex when a polygon or line is being edited. Only the feature
 * holding edit focus has vertex handles, so the rest of a multi-selection —
 * and any point or textbox — gets the deselect half alone.
 */
export const getSelectionInstruction = (
  selectedFeatureTypes: GeometryType[],
  isEditingPolygonOrLine: boolean,
): string | null => {
  if (isEmpty(selectedFeatureTypes)) {
    return null;
  }

  if (!isEditingPolygonOrLine) {
    return DESELECT_FEATURE_INSTRUCTION;
  }

  return `${DESELECT_FEATURE_INSTRUCTION} ${REMOVE_VERTEX_INSTRUCTION}`;
};

/**
 * The single instruction the banner shows, highest priority first.
 * A drawing tool's instruction says how to finish the geometry, so it waits
 * until a drawing is actually under way instead of greeting the tool click.
 */
export const getWorkspaceInstruction = ({
  activeTool,
  editingLock,
  selectedFeatureTypes,
  isDrawingInProgress,
  isEditingPolygonOrLine,
}: WorkspaceInstructionState): string | null => {
  if (editingLock === EditingLock.Hidden) {
    return EDITING_HANDLES_HIDDEN_INSTRUCTION;
  }

  if (isNil(activeTool)) {
    return null;
  }

  if (
    activeTool === WorkspaceTools.SelectFeatureTool &&
    !isEmpty(selectedFeatureTypes)
  ) {
    return getSelectionInstruction(
      selectedFeatureTypes,
      isEditingPolygonOrLine,
    );
  }

  if (
    DRAW_COMPLETION_INSTRUCTION_TOOLS.includes(activeTool) &&
    !isDrawingInProgress
  ) {
    return null;
  }

  return DRAWING_INSTRUCTION[activeTool] ?? null;
};

export const getToolboxTitle = ({
  label,
  keyHint,
}: ToolboxShortcutDefinition): string => {
  return `${label} (${keyHint})`;
};
